import { useEffect, useRef, useState, ReactNode, memo } from 'react'
import isEqual from 'fast-deep-equal/react'
import { useIntersection } from 'react-use'

interface UseVisibleRenderOptions {
  rootMargin?: string
  threshold?: number
  keepMounted?: boolean
}

interface VisibleRenderProps {
  children: ReactNode
  loading: ReactNode
  options?: UseVisibleRenderOptions
  dependencies?: any[]
  style?: React.CSSProperties
}

function VisibleRender({
  children,
  loading,
  options = {},
  dependencies = [],
  style,
}: VisibleRenderProps): ReactNode {
  const { rootMargin = '0px', threshold = 0.25, keepMounted = true } = options
  const [hasBeenVisible, setHasBeenVisible] = useState(false)
  const observerRef = useRef<HTMLDivElement>(null)
  const prevDependenciesRef = useRef<any[]>(dependencies)

  const intersection = useIntersection(observerRef, {
    rootMargin,
    threshold,
  })

  const isVisible = intersection?.isIntersecting ?? false

  useEffect(() => {
    if (isVisible && !hasBeenVisible) {
      setHasBeenVisible(true)
    }
  }, [isVisible, hasBeenVisible])

  useEffect(() => {
    if (keepMounted) {
      if (isEqual(dependencies, prevDependenciesRef.current)) return
      setHasBeenVisible(false)
      prevDependenciesRef.current = dependencies
    }
  }, dependencies)

  const shouldRender = keepMounted ? hasBeenVisible : isVisible

  return (
    <div ref={observerRef} style={style}>
      {shouldRender ? children : loading}
    </div>
  )
}

export default memo(VisibleRender)
